import {
  faChevronLeft,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Footer.css';

import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { AuthContext } from '../../shared/context/auth-context';
import { SupportSection } from './SupportSection';

type FooterProps = {
  onNextPage: (values?: unknown) => void;
  onPrevPage: () => void;
  iProgress: number;
};

export function Footer({ onNextPage, onPrevPage, iProgress }: FooterProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [lastPage, setLastPage] = useState(false);
  const [scrollControlIcon, setScrollControlIcon] = useState<IconDefinition | string>(
    faChevronDown
  );

  const navigateNextPage = () => {
    setScrollControlIcon(faChevronDown);
    onNextPage();
    setScrollControlIcon('down');
  };

  const navigatePrevPage = () => {
    setScrollControlIcon(faChevronDown);
    onPrevPage();
    setScrollControlIcon('down');
  };

  const scrollToBottom = () => {
    const rightContainer = document.getElementById('right_container');

    // Decide who should scroll
    const windowHasScroll = document.documentElement.scrollHeight > window.innerHeight;
    const rightContainerHasScroll = rightContainer?.scrollHeight > rightContainer.clientHeight;

    const useWindowScroll = windowHasScroll && !rightContainerHasScroll;

    // Determine scroll target
    let scrollTarget: 'window' | 'element' | null = null;
    if (useWindowScroll) {
      scrollTarget = 'window';
    } else if (rightContainer) {
      scrollTarget = 'element';
    }

    if (!scrollTarget) return; // nothing to scroll

    // Are we going down or up?
    const goingDown = scrollControlIcon === 'down' || scrollControlIcon === faChevronDown;

    // Compute the max scrollable top for the chosen target
    const maxTop =
      scrollTarget === 'window'
        ? document.documentElement.scrollHeight - window.innerHeight
        : rightContainer.scrollHeight - rightContainer.clientHeight;

    const top = goingDown ? maxTop : 0;

    // Perform the scroll
    if (scrollTarget === 'window') {
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      rightContainer.scrollTo({ top, behavior: 'smooth' });
    }

    // Toggle the icon
    setScrollControlIcon(goingDown ? 'up' : 'down');
  };

  useEffect(() => {
    if (iProgress) {
      if (iProgress === 13 && auth?.access === '2') {
        setLastPage(true);
      } else if (iProgress === 15) {
        setLastPage(true);
      } else {
        setLastPage(false);
      }
    }
  }, [iProgress, auth?.access]);

  const getNextButtonClassName = () => {
    if (lastPage) return 'last_page';
    if (iProgress) return `i${iProgress}`;
    return 'no_progress';
  };

  return (
    <footer className="main_footer" aria-label={t('landmark.footer.label')}>
      <div id="scroll_fade_bottom">
        <button
          type="button"
          id="down_icon"
          onClick={scrollToBottom}
          aria-label={`${t('FTR001')} ${scrollControlIcon === 'up' ? t('FTR003') : t('FTR002')}`}
        >
          <span className="visuallyHidden">
            {t('FTR001')}
            {scrollControlIcon === 'up' ? t('FTR003') : t('FTR002')}
          </span>
          <FontAwesomeIcon
            icon={scrollControlIcon === 'up' ? faChevronUp : faChevronDown}
            className="icon"
          />
        </button>
      </div>
      <div id="progress_footer_container">
        <div id="progress_footer" className="progress_footer">
          <div className="footer_buttons">
            {onPrevPage && (
              <Button
                onClick={navigatePrevPage}
                icon={{
                  faIconDefinition: faChevronLeft,
                  size: '3x',
                }}
                iconPosition="center"
                isDisabled={iProgress === 0}
                id="back"
                type="button"
                ariaLabel={t('assessment.button.back')}
              />
            )}

            {onNextPage && (
              <Button
                onClick={navigateNextPage}
                icon={
                  !lastPage
                    ? {
                        faIconDefinition: faChevronRight,
                        size: '3x',
                      }
                    : null
                }
                iconPosition="center"
                id="next"
                type="button"
                className={getNextButtonClassName()}
                ariaLabel={!lastPage ? t('assessment.button.next') : null}
              >
                {lastPage && (
                  <span id="generate_report">
                    {t('A116')}
                    <br />
                    {t('A117')}
                  </span>
                )}
              </Button>
            )}
          </div>
          <SupportSection />
        </div>
      </div>
    </footer>
  );
}
